import React, { useRef, useState, useEffect } from "react";
import { CardHeader } from "@/components/ui/card";
import { Chip } from "@/components/common/Chip";
import { cn } from "@/lib/utils";
import { Typography } from "@/components/ui/typography";
import { TooltipProvider } from "@radix-ui/react-tooltip";
import { Tooltip, TooltipContent, TooltipTrigger } from "../ui/tooltip";

type DashboardCardHeaderProps = {
  icon?: React.ReactNode;
  title: React.ReactNode;
  chip?: {
    key: string | React.ReactNode;
    value: string | number;
    variant?: "default" | "success" | "warning" | "error" | "danger";
    onClick?: () => void;
  };
  showStar?: boolean;
  button?: React.ReactNode;
  isFavourite?: boolean;
  onToggleFavourite?: () => void;
  className?: string;
};

const DashboardCardHeader: React.FC<DashboardCardHeaderProps> = ({
  icon,
  title,
  chip,
  showStar,
  button,
  isFavourite = false,
  onToggleFavourite,
  className,
}) => {
  const borderColorClass = isFavourite
    ? "border-[hsl(var(--dashboard-header-favourite-border))]"
    : "border-grey-200 dark:border-grey-700";
  const textRef = useRef<HTMLDivElement>(null);
  const [isOverflowing, setIsOverflowing] = useState(false);

  useEffect(() => {
    const checkOverflow = () => {
      if (textRef.current) {
        setIsOverflowing(
          textRef.current.scrollWidth > textRef.current.clientWidth
        );
      }
    };

    checkOverflow();
    window.addEventListener("resize", checkOverflow);

    return () => window.removeEventListener("resize", checkOverflow);
  }, [title]);

  const titleContent = (
    <div className='flex items-center gap-2 min-w-0 flex-1'>
      {icon && <div className='text-blue-500 flex-shrink-0'>{icon}</div>}
      <Typography ref={textRef} variant='semibold-16' className='truncate'>
        {title}
      </Typography>
    </div>
  );

  return (
    <CardHeader
      className={cn(
        "flex flex-row items-center justify-between px-4 py-3 border-b rounded-t-lg",
        "bg-[hsl(var(--navbar))] dark:bg-[hsl(var(--blue-800))]",
        borderColorClass,
        className
      )}>
      {/* Left side: icon + title */}
      {isOverflowing ? (
        <TooltipProvider delayDuration={0}>
          <Tooltip>
            <TooltipTrigger asChild className='cursor-pointer'>
              {titleContent}
            </TooltipTrigger>
            <TooltipContent
              side='top'
              align='start'
              className='max-w-xs bg-gray-700 text-grey-100 break-words z-50'>
              <Typography variant='regular-12' className='text-grey-100'>
                {title}
              </Typography>
            </TooltipContent>
          </Tooltip>
        </TooltipProvider>
      ) : (
        titleContent
      )}

      {/* Right side: button + star + chip */}
      <div className='flex items-center gap-4 mb-[3px] overflow-hidden flex-shrink-0 max-w-[50%]'>
        {chip && (
          <Chip
            label={chip.key}
            value={chip.value}
            variant={chip.variant || "default"}
            onClick={chip.onClick}
          />
        )}
        {button}
        {showStar && (
          <TooltipProvider delayDuration={0}>
            <Tooltip>
              <TooltipTrigger asChild>
                <button
                  type='button'
                  onClick={(e) => {
                    e.stopPropagation();
                    if (onToggleFavourite) {
                      onToggleFavourite();
                    }
                  }}
                  aria-pressed={isFavourite}
                  className={cn(
                    "p-1 transition-colors bg-transparent cursor-pointer",
                    isFavourite
                      ? "text-[hsl(var(--dashboard-header-favourite-text))]"
                      : "text-grey-800 dark:text-grey-100 hover:text-warning dark:hover:text-warning"
                  )}>
                  <i
                    className={cn(
                      "text-[18px]",
                      isFavourite ? "ri-star-fill" : "ri-star-line"
                    )}
                  />
                </button>
              </TooltipTrigger>
              <TooltipContent
                side='top'
                align='end'
                className='max-w-xs bg-gray-700 text-grey-100 break-words z-50'>
                <Typography variant='regular-12' className='text-grey-100'>
                  {isFavourite ? "Remove from favourites" : "Mark as favourite"}
                </Typography>
              </TooltipContent>
            </Tooltip>
          </TooltipProvider>
        )}
      </div>
    </CardHeader>
  );
};

export default DashboardCardHeader;
